import * as React from 'react';
import { Dispatch } from 'redux';
import axios, { AxiosError } from 'axios';
import { SafActionsMenu, receiveAxiosError, userHasAnyRole } from '@konicaminolta/common-react';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { ReportAlias } from '../../types/ReportAlias/ReportAlias';
import AddEditAliasModal from './Modals/AddEditAliasModal';
import { faPenToSquare, faTrash } from '@fortawesome/pro-solid-svg-icons';
import DeleteAliasModal from './Modals/DeleteAliasModal';
import { IPowerBiReportViewerObject } from '../../types/Common/IPowerBiReportViewerObject';
import { PowerBiObjectType } from '../../types/Common/PowerBiObjectType';
import { User } from 'oidc-client';
import { ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER } from '../../appRoles';

type ReportAliasesTableProps = {
    dispatch: Dispatch;
    reportId: string;
    refreshReport: any;
    user: User;
}

interface ReportAliasesTableState {
    data: ReportAlias[];
    isLoading: boolean;
    hasPermissions: boolean;
    showAddAliasModal: boolean;
    showDeleteAliasModal: boolean;
    showEditAliasModal: boolean;
    isAliasModalAddMode: boolean;
    reportAlias: ReportAlias | undefined;
}


class ReportAliasesTable extends React.Component<ReportAliasesTableProps, ReportAliasesTableState> {
    public constructor(props: ReportAliasesTableProps) {
        super(props);
        this.state = {
            data: [],
            isLoading: true,
            hasPermissions: true,
            showAddAliasModal: false,
            showDeleteAliasModal: false,
            showEditAliasModal: false,
            isAliasModalAddMode: true,
            reportAlias: undefined,
        };
    }


    public componentDidMount() {
        this.getReportAliasesRecords();
    }

    private getReportAliasesRecords() {
        axios
            .get<ReportAlias[]>(
                `./api/reports/${this.props.reportId}/aliases`
            )
            .then((response) => {
                const { data } = response;
                this.setState({ data: data, isLoading: false, hasPermissions: true });
            })
            .catch((e: AxiosError) => {
                if (e.response && e.response.status == 401) {
                    this.setState({hasPermissions: false});
                }
                else if (e.response && e.response.status !== 404) {
                    this.props.dispatch(receiveAxiosError({ error: e }));
                }
                this.setState({ isLoading: false });
            });
    }

    private handleOpenAddAliasModal() {
        this.setState({ isAliasModalAddMode: true });
        this.setState({ showAddAliasModal: true });
    }

    private handleCloseAddAliasModal() {
        this.setState({ showAddAliasModal: false });
    }

    private handleRefreshAliasList() {
        this.setState({isLoading: true});
        this.getReportAliasesRecords();
        this.handleCloseAddAliasModal();
        this.props.refreshReport();
    };

    private handleOpenEditAliasModal(reportAlias: ReportAlias) {
        this.setState({ 
            isAliasModalAddMode: false,
            reportAlias: reportAlias,
        });
        this.setState({ showAddAliasModal: true });
    }

    private handleDeleteAliasModalOpen(reportAlias: ReportAlias) {
        this.setState({reportAlias: reportAlias});
        this.setState({showDeleteAliasModal: true });
        
        this.handleRefreshAliasList();
    }

    
    private handleDeleteAliasModalOkButton() {
        this.handleRefreshAliasList();
        this.handleCloseDeleteAliasModal();
    }

    // Close and clear ReportAlias handle
    private handleCloseDeleteAliasModal() {
        this.setState({showDeleteAliasModal: false});
        this.setState({reportAlias: undefined});
    }

    render() {
        const NoDataComponent: React.FunctionComponent = (): React.ReactElement => {
            if (!this.state.isLoading && !this.state.hasPermissions) {
                return (
                    <div className="d-flex justify-content-center mt-3" data-cy="bootstrap-table-loaded">
                        <p className="text-center">Insufficient permissions to view aliases.</p>
                    </div>
                );
            }
            if (!this.state.isLoading) {
                return (
                    <div className="d-flex justify-content-center mt-3" data-cy="bootstrap-table-loaded">
                        <p className="text-center">No report aliases found.</p>
                    </div>
                );
            }
            else {
                return (
                    <div className="d-flex justify-content-center mt-3">
                        <Spinner animation="border" variant="primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>
                );
            }
        };

        const { SearchBar } = Search;

        const columns = [
            {
                dataField: 'id',
                text: 'Id',
                sort: true,
            },
            {
                dataField: 'aliasName',
                text: 'Alias Name',
                sort: true,
            },
            {
                dataField: 'isDefault',
                text: 'Default',
                sort: true,
                formatter: (cell: boolean, row: ReportAlias) => {
                    return row.isDefault ? "Yes" : "";
                },
            },
            {
                dataField: 'isHidden',
                text: 'Hidden',
                sort: true,
                formatter: (cell: boolean, row: ReportAlias) => {
                    return row.isHidden ? "Yes" : "No";
                },
            },
            {
                dataField: '',
                text: '',
                sort: false,
                formatter: (cell: any, row: ReportAlias) => {
                    if(userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER])){
                        return (<SafActionsMenu
                            menuItems={[
                                {
                                    icon: faPenToSquare,
                                    label: 'Edit',
                                    onClick: () => {
                                        this.handleOpenEditAliasModal(row);
                                    },
                                },
                                {
                                    icon: faTrash,
                                    label: 'Delete',
                                    onClick: () => {
                                        this.handleDeleteAliasModalOpen(row);
                                    }
                                },
                            ]}
                            id={row.id} />
                        );
                    }              
                }
            }
        ];


        return (
            <React.Fragment>
                <Row>
                    <Col>
                        <Card className="panel">
                            <Card.Header>
                                <Row>
                                    <Col md={6}>
                                        <h3>Aliases</h3>
                                    </Col>
                                    <Col md={6} className='text-end'>
                                        <Button 
                                            variant={userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER]) ? 'primary' : 'secondary' }
                                            className='ms-3' 
                                            onClick={() => this.handleOpenAddAliasModal()}
                                            disabled={!userHasAnyRole(this.props.user, [ROLE_FULL_ADMINISTRATOR, ROLE_POWER_USER])}
                                        >
                                            Add Alias
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Header>
                            <Card.Body>
                                <ToolkitProvider
                                    keyField="id"
                                    search
                                    data={this.state.data}
                                    columns={columns}
                                >
                                    {
                                        props => (
                                            <React.Fragment>
                                                <Row>
                                                    <Col>
                                                        <SearchBar {...props.searchProps} />
                                                    </Col>
                                                </Row>
                                                <BootstrapTable
                                                    {...props.baseProps}
                                                    id="reportAliasesTable"
                                                    classes='clickable'
                                                    striped
                                                    hover
                                                    bordered={false}
                                                    defaultSorted={[{ dataField: 'aliasName', order: 'asc' }]}
                                                    pagination={paginationFactory({ sizePerPage: 10 })}
                                                    noDataIndication={() => <NoDataComponent />}
                                                />
                                            </React.Fragment>
                                        )
                                    }
                                </ToolkitProvider>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {
                    <React.Fragment>
                        <AddEditAliasModal
                            dispatch={this.props.dispatch}
                            powerBiObject={{id:this.props.reportId, hoursUntilStale: 0, type:PowerBiObjectType.Report} as IPowerBiReportViewerObject}
                            addAlias={() => this.handleRefreshAliasList()}
                            hideModal={() => this.handleCloseAddAliasModal()}
                            showModal={this.state.showAddAliasModal}
                            isAddAliasMode={this.state.isAliasModalAddMode}
                            reportAlias={this.state.reportAlias}
                            />
                    </React.Fragment>
                }
                {
                    <React.Fragment>
                        <DeleteAliasModal
                            dispatch={this.props.dispatch}
                            reportAlias={this.state.reportAlias}
                            modalBody={`Delete report alias: ${this.state.reportAlias?.aliasName}`}
                            okButton={() => this.handleDeleteAliasModalOkButton()}
                            hideModal={() => this.handleCloseDeleteAliasModal()}
                            showModal={this.state.showDeleteAliasModal}
                        />
                    </React.Fragment>
                }

            </React.Fragment>
        );
    }
}

export default ReportAliasesTable;